// Scouting report engine (2026-10-16): turns text copied from GameChanger's web "Plays" and
// "Box score" pages into a compact per-game record, then turns a set of those game records into a
// scouting report for one opponent.
//
// Pure ESM, no dependencies, no Firebase -- so the same file runs in the browser (admin paste flow
// in App.jsx), in node:test (test/scouting.test.mjs), and later in Cloud Functions if/when the
// GameChanger pull is automated (sync-shared-to-functions.cjs copies every shared/*.mjs there).
//
// Input is whatever lands on the clipboard from Ctrl+A / Ctrl+C on the GC page (or the page's
// innerText) -- nav, header and footer noise is tolerated. GC lists plays newest-first by default;
// both orders are handled.
//
// Everything here is deterministic: same games in, same report out. "Do this" text comes from the
// fixed rules in buildScoutingReport (documented in the report's own notes), not from an AI model.

export const SCOUTING_PARSER_VERSION = 1;

// ─── small helpers ────────────────────────────────────────────────────────────────────────────

const HALF_RE = /^(Top|Bottom)\s+(\d+)(?:st|nd|rd|th)\s+-\s+(.+?)\s*$/;
const DATE_RE = /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\w*\s+([A-Z][a-z]{2})\w*\s+(\d{1,2})(?:,\s*(\d{1,2}:\d{2}\s*[AP]M))?/;
const MONTHS = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
const DOWS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const cleanLines = (text) =>
  String(text || "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((l) => l.replace(/ /g, " ").replace(/[­​]/g, "").trim());

export const normName = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9#]+/g, " ").trim();
const slug = (s) => normName(s).replace(/\s+/g, "-").slice(0, 40);
const pct = (n, d) => (d > 0 ? Math.round((100 * n) / d) : null);

// GC's date line has no year ("Sat Oct 3, 3:30 PM ..."). Pick the most recent year (not more than a
// month in the future) whose calendar puts that date on that weekday -- unambiguous in practice.
export function inferGameDate(dateLine, now = new Date()) {
  const m = DATE_RE.exec(dateLine || "");
  if (!m) return null;
  const dow = DOWS[m[1].slice(0, 3)], mon = MONTHS[m[2]], day = +m[3];
  if (mon === undefined) return null;
  const limit = new Date(now.getTime() + 31 * 864e5);
  for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 6; y--) {
    const d = new Date(Date.UTC(y, mon, day));
    if (d > limit) continue;
    if (d.getUTCDay() === dow) return { iso: d.toISOString().slice(0, 10), time: m[4] || "" };
  }
  return { iso: null, time: m[4] || "" };
}

function parseHeader(lines) {
  const dateLine = lines.find((l) => DATE_RE.test(l)) || "";
  const halves = lines.filter((l) => HALF_RE.test(l)).map((l) => HALF_RE.exec(l));
  const top = halves.find((h) => h[1] === "Top");
  const bottom = halves.find((h) => h[1] === "Bottom");
  let away = top ? top[3] : null, home = bottom ? bottom[3] : null;
  if (!away || !home) {
    // Fall back to the "5 - 11" score line, which GC follows with away then home team names.
    const i = lines.findIndex((l) => /^\d+\s+-\s+\d+$/.test(l));
    if (i >= 0) {
      const names = lines.slice(i + 1).filter(Boolean).slice(0, 2);
      away = away || names[0] || null;
      home = home || names[1] || null;
    }
  }
  return { dateLine, away, home };
}

// ─── pitch sequences ─────────────────────────────────────────────────────────────────────────

// Pitch codes: B ball, K called strike, S swinging strike, F foul, X in play.
function pitchCode(tok) {
  if (/^ball\b/i.test(tok) || /^intentional ball/i.test(tok)) return "B";
  if (/^strike\b.*\blooking\b/i.test(tok)) return "K";
  if (/^strike\b/i.test(tok)) return "S";
  if (/^foul\b/i.test(tok)) return "F";
  if (/^in play\b/i.test(tok)) return "X";
  return null;
}

const PITCH_CHANGE_RE = /^(.*?)\s*in for pitcher\s*(.*?)$/i;

// Splits a sequence line into pitches plus the side events GC interleaves (steals, wild pitches,
// lineup/courtesy-runner changes, pitching changes).
function parseSequence(line) {
  const out = { pitches: "", wp: 0, pb: 0, pitchChange: null, pitchChangeAt: -1 };
  if (!line) return out;
  const toks = line.replace(/\.$/, "").split(/,\s*/).map((t) => t.trim()).filter(Boolean);
  let wpThisGap = false, pbThisGap = false;
  for (const tok of toks) {
    const c = pitchCode(tok);
    if (c) { out.pitches += c; wpThisGap = false; pbThisGap = false; continue; }
    // One wild pitch can move several runners ("advances to 3rd on wild pitch, scores on wild
    // pitch") -- count it once per gap between pitches, which matches GC's own WP totals.
    if (/wild pitch/i.test(tok) && !wpThisGap) { out.wp++; wpThisGap = true; }
    if (/passed ball/i.test(tok) && !pbThisGap) { out.pb++; pbThisGap = true; }
    const pc = PITCH_CHANGE_RE.exec(tok);
    if (pc && !/lineup changed/i.test(tok)) {
      out.pitchChange = [pc[1].trim() || null, pc[2].trim() || null];
      out.pitchChangeAt = out.pitches.length;
    }
  }
  return out;
}

// ─── result lines ────────────────────────────────────────────────────────────────────────────

const PA_VERB_RE = /\b(strikes out|walks|is walked|intentionally walked|singles|doubles|triples|homers|hits an? (?:inside the park )?home run|grounds|flies|lines (?:out|into)|pops|is hit by pitch|reaches|sacrifices|out at first|bunts|hits into|hits a|fouls out|is out|interference)\b/i;

const NAME_PARTICLES = new Set(["de", "la", "del", "van", "von", "der", "da", "di", "le", "dos", "das", "st", "st."]);

// Leading capitalized tokens of the result line = batter name. GC leaves the name blank for
// players with no name on the roster, so the line then starts with the (lowercase) verb.
function leadingName(line) {
  const toks = line.split(/\s+/);
  const name = [];
  for (let i = 0; i < toks.length; i++) {
    const t = toks[i];
    if (/^[A-Z#0-9][\w'’.\-]*$/.test(t)) { name.push(t); continue; }
    if (NAME_PARTICLES.has(t.toLowerCase()) && name.length && i + 1 < toks.length && /^[A-Z]/.test(toks[i + 1])) { name.push(t); continue; }
    break;
  }
  return name.length ? name.join(" ") : null;
}

const FIELDERS = [
  ["LF", /\bleft field(?:er)?\b/i], ["CF", /\bcenter field(?:er)?\b/i], ["RF", /\bright field(?:er)?\b/i],
  ["SS", /\bshortstop\b/i], ["3B", /\bthird baseman\b/i], ["2B", /\bsecond baseman\b/i],
  ["1B", /\bfirst baseman\b/i], ["P", /\bpitcher\b/i], ["C", /\bcatcher\b/i],
];
export const ZONE = { "3B": "L", SS: "L", LF: "L", P: "M", C: "M", CF: "M", "1B": "R", "2B": "R", RF: "R" };
export const OUTFIELD = new Set(["LF", "CF", "RF"]);

function firstFielder(line) {
  let best = null, at = Infinity;
  for (const [pos, re] of FIELDERS) {
    const m = re.exec(line);
    if (m && m.index < at) { at = m.index; best = pos; }
  }
  return best;
}

function bipType(text, title) {
  const s = `${text} ${title}`;
  if (/\bbunt/i.test(s) || /\bsacrifices\b/i.test(text)) return "BU";
  if (/hard ground ball/i.test(s)) return "HGB";
  if (/ground ball|grounds/i.test(s)) return "GB";
  if (/line drive|lines/i.test(s)) return "LD";
  if (/pop ?(?:up|fly)|pops/i.test(s)) return "PU";
  if (/fly ball|flies/i.test(s)) return "FB";
  return null;
}

// Outcome code from GC's event title, falling back to the result text.
function outcome(title, text) {
  const t = (title || "").toLowerCase();
  const map = [
    [/^single/, "1B"], [/^double play/, "DP"], [/^double/, "2B"], [/^triple play/, "TP"], [/^triple/, "3B"],
    [/^home run|^inside the park/, "HR"], [/^intentional walk/, "BB"], [/^walk/, "BB"], [/^hit by pitch/, "HBP"],
    [/^strikeout/, "K"], [/^dropped 3rd strike/, "K"], [/^error/, "ROE"], [/^fielder'?s choice/, "FC"],
    [/^sacrifice bunt/, "SAC"], [/^sacrifice fly/, "SF"], [/^ground out/, "GO"], [/^fly out|^foul out/, "FO"],
    [/^line out/, "LO"], [/^pop out|^infield fly/, "PO"], [/^catcher'?s interference/, "CI"],
  ];
  for (const [re, c] of map) if (re.test(t)) return c;
  const x = (text || "").toLowerCase();
  if (/\bsingles\b/.test(x)) return "1B";
  if (/\bdoubles\b/.test(x)) return "2B";
  if (/\btriples\b/.test(x)) return "3B";
  if (/\bhomers\b|home run/.test(x)) return "HR";
  if (/\bwalks\b|walked/.test(x)) return "BB";
  if (/hit by pitch/.test(x)) return "HBP";
  if (/strikes out|dropped 3rd strike/.test(x)) return "K";
  if (/\berror\b/.test(x)) return "ROE";
  if (/fielder'?s choice/.test(x)) return "FC";
  if (/sacrifices/.test(x)) return "SAC";
  if (/grounds (?:out|into)/.test(x)) return "GO";
  if (/flies out|fouls out/.test(x)) return "FO";
  if (/lines out/.test(x)) return "LO";
  if (/pops out/.test(x)) return "PO";
  return "OUT";
}

export const HIT_TB = { "1B": 1, "2B": 2, "3B": 3, HR: 4 };
const NOT_AB = new Set(["BB", "HBP", "SAC", "SF", "CI"]);
const IN_PLAY = new Set(["1B", "2B", "3B", "HR", "ROE", "FC", "SAC", "SF", "GO", "FO", "LO", "PO", "DP", "TP", "OUT"]);

// ─── plays page ──────────────────────────────────────────────────────────────────────────────

export function parsePlays(text) {
  const lines = cleanLines(text);
  const header = parseHeader(lines);
  const warnings = [];
  // Group into half-innings: header line, then events. An event = title line (no trailing period)
  // followed by its body lines (each ending in a period): pitch sequence, then result text.
  const halves = [];
  let cur = null, ev = null;
  const closeEv = () => { if (ev && cur) cur.events.push(ev); ev = null; };
  for (const l of lines) {
    if (!l) continue;
    const h = HALF_RE.exec(l);
    if (h) { closeEv(); cur = { half: h[1] === "Top" ? "T" : "B", inning: +h[2], team: h[3], events: [] }; halves.push(cur); continue; }
    if (!cur) continue;
    if (/\sat bat$/.test(l) && !/\.$/.test(l)) { closeEv(); continue; } // in-progress PA, no result yet
    if (/\.$/.test(l)) { if (ev) ev.body.push(l); continue; }
    closeEv();
    ev = { title: l.split("|")[0].trim(), body: [] };
  }
  closeEv();

  // GC defaults to newest-first; normalize to game order.
  const order = (x) => x.inning * 2 + (x.half === "B" ? 1 : 0);
  const reversed = halves.length > 1 && order(halves[0]) > order(halves[halves.length - 1]);
  if (reversed) { halves.reverse(); halves.forEach((h) => h.events.reverse()); }

  const pas = [];
  for (const h of halves) {
    for (const e of h.events) {
      if (!e.body.length) continue;
      if (/^runner out$/i.test(e.title)) {
        // Base-running outs aren't plate appearances, but can still carry WP/PB info.
        const seq = parseSequence(e.body.join(", "));
        if (seq.wp || seq.pb) pas.push({ nonPA: true, half: h.half, inning: h.inning, team: h.team, wp: seq.wp, pb: seq.pb, pitches: "" });
        continue;
      }
      const resultIdx = e.body.findIndex((b) => PA_VERB_RE.test(b) && !/^(ball|strike|foul|in play)\b/i.test(b));
      if (resultIdx < 0) { warnings.push(`Skipped unrecognized play "${e.title}" (${h.half === "T" ? "Top" : "Bottom"} ${h.inning})`); continue; }
      const seqLine = e.body.slice(0, resultIdx).join(", ");
      const result = e.body.slice(resultIdx).join(" ");
      const seq = parseSequence(seqLine);
      // Result-line wild pitches / passed balls ("reaches on dropped 3rd strike (wild pitch)").
      const rwp = /wild pitch/i.test(result) ? 1 : 0, rpb = /passed ball/i.test(result) ? 1 : 0;
      const pm = /(?:^|,)\s*([^,]*?)\s*\bpitching\b/i.exec(result);
      const o = outcome(e.title, result);
      const fielder = IN_PLAY.has(o) ? firstFielder(result) : null;
      pas.push({
        half: h.half, inning: h.inning, team: h.team,
        batter: leadingName(result),
        outcome: o,
        pitches: seq.pitches,
        fielder,
        bip: fielder ? bipType(result, e.title) : null,
        bunt: o === "SAC" || /\bbunt/i.test(`${e.title} ${result}`),
        pitcherExplicit: pm ? (pm[1].trim() || "") : undefined, // "" = GC left the pitcher's name blank
        pitchChange: seq.pitchChange, pitchChangeAt: seq.pitchChangeAt,
        wp: seq.wp + rwp, pb: seq.pb + rpb,
      });
    }
  }
  return { ...header, reversed, halves: halves.length, pas, warnings };
}

// Works out who was pitching each PA for the team in the field. GC only names the pitcher on some
// results ("..., Kloie Iboy pitching"), so: within each stretch between pitching changes, the first
// explicitly-named pitcher owns the whole stretch; a stretch with no name inherits the previous one
// (or, right after a change, whichever of the two names in "A in for pitcher B" isn't the outgoing
// pitcher -- GC's wording for that is inconsistent about which side is the new one).
function assignPitchers(pas) {
  const real = pas.filter((p) => !p.nonPA);
  // Build segments: break before a PA whose sequence contains a pitching change, and at the start
  // of each new half-inning (so a mid-game swap between innings is picked up from the next name).
  const segs = [];
  let seg = null, lastKey = null;
  for (const p of real) {
    const key = `${p.inning}${p.half}`;
    if (!seg || p.pitchChange || key !== lastKey) { seg = { pas: [], change: p.pitchChange || null, newHalf: key !== lastKey }; segs.push(seg); }
    seg.pas.push(p);
    lastKey = key;
  }
  let current;
  for (const s of segs) {
    const named = s.pas.find((p) => p.pitcherExplicit !== undefined);
    let owner;
    if (s.change && !named) {
      const [a, b] = s.change;
      owner = a && a !== current ? a : b && b !== current ? b : a || b || current;
    } else if (named) {
      owner = named.pitcherExplicit;
      // A half-inning that opens with unnamed PAs keeps the previous pitcher for those PAs only if
      // the name we just found can't be confirmed as a change -- we have no change token, so the
      // look-ahead name wins (it's who threw the first named pitch of this stretch).
    } else {
      owner = current;
    }
    for (const p of s.pas) p.pitcher = p.pitcherExplicit !== undefined ? p.pitcherExplicit : owner;
    const lastNamed = [...s.pas].reverse().find((p) => p.pitcherExplicit !== undefined);
    current = lastNamed ? lastNamed.pitcherExplicit : owner;
  }
  // Non-PA wild pitches go to whoever pitched the PA just before them.
  let prev;
  for (const p of pas) { if (p.nonPA) p.pitcher = prev ? prev.pitcher : undefined; else prev = p; }
}

// ─── box score page ──────────────────────────────────────────────────────────────────────────

const NUM_LINE = /^#\s?(\d{1,3})\b\s*(?:\((.*)\))?/;
const isStatVal = (l) => /^-?\d+(\.\d+)?$/.test(l);

export function parseBoxScore(text) {
  const lines = cleanLines(text).filter(Boolean);
  const teams = {};
  for (let i = 0; i < lines.length; i++) {
    if (lines[i + 1] !== "LINEUP") continue;
    const team = { name: lines[i], lineup: [], pitching: [], pitchesStrikes: {}, battersFaced: {}, wp: {}, hbp: {} };
    teams[lines[i]] = team;
    let j = i + 2;
    const skipColumnHeaders = () => { while (j < lines.length && /^[A-Z]{1,4}$/.test(lines[j]) && lines[j] !== "TEAM") j++; }; // AB R H RBI ... / IP H R ER ...
    skipColumnHeaders();
    // Rows: [Name]\n#NN (pos)\n6 numbers ... until TEAM
    const readRows = (into, stop) => {
      while (j < lines.length && lines[j] !== "TEAM" && !stop(lines[j])) {
        let name = null;
        if (!NUM_LINE.test(lines[j]) && !isStatVal(lines[j])) { name = lines[j]; j++; }
        const nm = NUM_LINE.exec(lines[j] || "");
        let num = null, pos = "";
        if (nm) { num = nm[1]; pos = nm[2] || ""; j++; }
        const vals = [];
        while (j < lines.length && isStatVal(lines[j]) && vals.length < 6) vals.push(lines[j++]);
        if (!name && !num) { j++; continue; }
        into.push({ name, num, pos, vals });
      }
    };
    readRows(team.lineup, () => false);
    while (j < lines.length && lines[j] !== "PITCHING" && lines[j + 1] !== "LINEUP") j++;
    if (lines[j] !== "PITCHING") continue;
    j++;
    skipColumnHeaders();
    readRows(team.pitching, () => false);
    // Footer lines after the pitching table, up to the next team's block:
    // "Pitches-Strikes: A 54-35, B 39-20", "Batters Faced: A 14", "WP: A 2", "HBP: A"
    for (let k = j; k < lines.length && lines[k + 1] !== "LINEUP"; k++) {
      const f = /^(Pitches-Strikes|Batters Faced|WP|HBP):\s*(.*)$/.exec(lines[k]);
      if (!f) continue;
      for (const part of f[2].split(/,\s*/)) {
        if (f[1] === "Pitches-Strikes") { const m = /^(.*\S)\s+(\d+)-(\d+)$/.exec(part); if (m) team.pitchesStrikes[m[1]] = { pitches: +m[2], strikes: +m[3] }; }
        else { const m = /^(.*?)(?:\s+(\d+))?$/.exec(part); if (m) (f[1] === "Batters Faced" ? team.battersFaced : f[1] === "WP" ? team.wp : team.hbp)[m[1]] = m[2] ? +m[2] : 1; }
      }
    }
  }
  // Uniform number lookup by name, from both batting and pitching rows.
  for (const t of Object.values(teams)) {
    t.numbers = {};
    for (const r of [...t.lineup, ...t.pitching]) if (r.name && r.num) t.numbers[r.name] = r.num;
  }
  return { teams };
}

// ─── one game: plays + (optional) box score → stored record ─────────────────────────────────

// Fuzzy pick of which of the two teams in the game is the opponent being scouted.
export function guessScoutedTeam(names, opponentName) {
  const target = normName(opponentName).split(" ").filter(Boolean);
  let best = null, bestScore = -1;
  for (const n of names.filter(Boolean)) {
    const words = new Set(normName(n).split(" "));
    const score = target.filter((w) => words.has(w)).length / Math.max(1, target.length);
    if (score > bestScore) { best = n; bestScore = score; }
  }
  return bestScore > 0 ? best : null;
}

export function buildGameRecord({ playsText, boxText = "", scoutedTeam, now = new Date() }) {
  const plays = parsePlays(playsText);
  if (!plays.pas.length) throw new Error("No plays found. Copy the whole Plays page from GameChanger (Ctrl+A, Ctrl+C) and paste it here.");
  const teamNames = [plays.away, plays.home].filter(Boolean);
  if (!teamNames.includes(scoutedTeam)) throw new Error(`"${scoutedTeam}" isn't one of the teams in this game (${teamNames.join(" vs ")}).`);
  const other = teamNames.find((n) => n !== scoutedTeam) || "";
  const date = inferGameDate(plays.dateLine, now) || { iso: null, time: "" };

  const offense = plays.pas.filter((p) => p.team === scoutedTeam);
  const defense = plays.pas.filter((p) => p.team !== scoutedTeam);
  assignPitchers(defense);

  const box = boxText ? parseBoxScore(boxText) : { teams: {} };
  const boxUs = box.teams[scoutedTeam] || null;
  const boxThem = box.teams[other] || null;
  const warnings = [...plays.warnings];
  if (boxText && !boxUs) warnings.push(`Box score didn't contain "${scoutedTeam}" -- check you pasted the box score for this same game.`);

  // ── validation against GC's own box score totals ──
  const checks = [];
  const offPA = offense.filter((p) => !p.nonPA).length;
  if (boxThem && Object.keys(boxThem.battersFaced).length) {
    const bf = Object.values(boxThem.battersFaced).reduce((a, b) => a + b, 0);
    checks.push({ what: `${scoutedTeam} plate appearances`, parsed: offPA, box: bf, ok: bf === offPA });
  }
  const perPitcher = {};
  for (const p of defense) {
    if (p.nonPA) { const k = p.pitcher ?? ""; (perPitcher[k] ||= { bf: 0, pitches: 0, strikes: 0 }); continue; }
    const k = p.pitcher ?? "";
    const s = (perPitcher[k] ||= { bf: 0, pitches: 0, strikes: 0 });
    s.bf++;
    s.pitches += p.pitches.length; // GC's own pitch counts don't add a pitch for an HBP (verified against box scores)
    s.strikes += [...p.pitches].filter((c) => c !== "B").length;
  }
  if (boxUs && Object.keys(boxUs.pitchesStrikes).length) {
    const boxNames = Object.keys(boxUs.pitchesStrikes);
    const parsedNames = Object.keys(perPitcher);
    const unmatchedBox = boxNames.filter((n) => !perPitcher[n]);
    for (const name of parsedNames) {
      // An unnamed pitcher in the plays shows up in the box score as "#NN" -- pair them up when
      // there's exactly one of each left over.
      const boxName = boxUs.pitchesStrikes[name] ? name : (name === "" && unmatchedBox.length === 1 ? unmatchedBox[0] : null);
      if (!boxName) { checks.push({ what: `Pitcher ${name || "(unnamed)"} pitch count`, parsed: perPitcher[name].pitches, box: null, ok: false }); continue; }
      const b = boxUs.pitchesStrikes[boxName], bf = boxUs.battersFaced[boxName];
      checks.push({ what: `${name || boxName} pitches`, parsed: perPitcher[name].pitches, box: b.pitches, ok: b.pitches === perPitcher[name].pitches });
      checks.push({ what: `${name || boxName} strikes`, parsed: perPitcher[name].strikes, box: b.strikes, ok: b.strikes === perPitcher[name].strikes });
      if (bf != null) checks.push({ what: `${name || boxName} batters faced`, parsed: perPitcher[name].bf, box: bf, ok: bf === perPitcher[name].bf });
    }
  }

  const compact = (p) => {
    const o = { i: p.inning, o: p.outcome, p: p.pitches };
    if (p.nonPA) { o.np = 1; o.o = "RUN"; }
    if (p.batter) o.b = p.batter;
    if (p.fielder) o.f = p.fielder;
    if (p.bip) o.t = p.bip;
    if (p.bunt) o.bu = 1;
    if (p.wp) o.wp = p.wp;
    if (p.pb) o.pb = p.pb;
    return o;
  };
  const firstDef = defense.find((p) => !p.nonPA);
  return {
    key: `${date.iso || "nodate"}_${(date.time || "").replace(/\W+/g, "")}_${slug(plays.away)}_${slug(plays.home)}`,
    parserVersion: SCOUTING_PARSER_VERSION,
    dateLabel: plays.dateLine.split(" - ")[0] || plays.dateLine,
    date: date.iso,
    away: plays.away, home: plays.home,
    scoutedTeam, vs: other,
    offense: offense.map(compact),
    defense: defense.map((p) => ({ ...compact(p), pi: p.pitcher === undefined ? null : p.pitcher })),
    starter: firstDef ? (firstDef.pitcher ?? null) : null,
    numbers: boxUs ? boxUs.numbers : {},
    checks,
    warnings,
  };
}

// ─── report ──────────────────────────────────────────────────────────────────────────────────

const UNNAMED = "__unnamed__";

function batterStats(pas) {
  const s = { pa: 0, ab: 0, h: 0, tb: 0, bb: 0, hbp: 0, sf: 0, k: 0, xbh: 0, bunts: 0, L: 0, M: 0, R: 0, bip: 0, ofAir: 0, fp: 0, fpTake: 0 };
  for (const p of pas) {
    if (p.np) continue;
    s.pa++;
    if (!NOT_AB.has(p.o)) s.ab++;
    if (HIT_TB[p.o]) { s.h++; s.tb += HIT_TB[p.o]; if (p.o !== "1B") s.xbh++; }
    if (p.o === "BB") s.bb++;
    if (p.o === "HBP") s.hbp++;
    if (p.o === "SF") s.sf++;
    if (p.o === "K") s.k++;
    if (p.bu) s.bunts++;
    if (p.f && ZONE[p.f]) { s.bip++; s[ZONE[p.f]]++; if (OUTFIELD.has(p.f) && (p.t === "FB" || p.t === "LD")) s.ofAir++; }
    if (p.p && p.p.length) { s.fp++; if (p.p[0] === "B" || p.p[0] === "K") s.fpTake++; }
  }
  const obpD = s.ab + s.bb + s.hbp + s.sf;
  s.obp = obpD ? (s.h + s.bb + s.hbp) / obpD : 0;
  s.slg = s.ab ? s.tb / s.ab : 0;
  s.ops = s.obp + s.slg;
  s.takePct = pct(s.fpTake, s.fp);
  return s;
}

const SIDE_WORDS = { L: ["left", "3B, SS, LF"], M: ["middle", "CF, SS, 2B"], R: ["right", "1B, 2B, RF"] };

// The fixed "Do this" rules. Kept in one place, and echoed into the report's notes, so a coach can
// see exactly why a line says what it says.
export const LOOKBACK_DAYS = 60; // report only uses opponent games from this many days before the Warriors game

// Which saved games a report should use: dated within `lookbackDays` before `asOf` (the Warriors
// game date, or today), and not after it. Undated games are left out -- they can't be placed.
export function gamesInWindow(games, { asOf = new Date(), lookbackDays = LOOKBACK_DAYS } = {}) {
  const end = new Date(asOf); end.setUTCHours(23, 59, 59, 999);
  const start = new Date(end.getTime() - lookbackDays * 864e5); start.setUTCHours(0, 0, 0, 0);
  const startIso = start.toISOString().slice(0, 10), endIso = end.toISOString().slice(0, 10);
  const used = [], excluded = [];
  for (const g of games) (g.date && g.date >= startIso && g.date <= endIso ? used : excluded).push(g);
  return { used, excluded, startIso, endIso };
}

export const RULES = {
  minBipForShade: 6,       // need 6+ balls in play before recommending a shade
  shadePct: 50,            // one side (or the middle) gets 50%+ of balls in play → shade / stack
  hardShadePct: 60,        // 60%+ → called out in the DEFENSE summary as "shade hard"
  ofDeepXbh: 2,            // 2+ extra-base hits → OF deep
  ofDeepAirPct: 40,        // or 40%+ of balls in play are fly balls / liners to the OF
  ofInMaxAirPct: 15,       // no XBH and ≤15% air balls to the OF → OF in
  buntsForCornersIn: 2,    // 2+ bunts → corners in
  dontGrooveSwingPct: 45,  // swings at 45%+ of first pitches → don't groove strike 1
  throwStrikeTakePct: 70,  // takes 70%+ of first pitches → throw strike 1
  minFirstPitches: 6,
  fewPA: 10,               // flagged "few PA" below this
  othersMaxPA: 4,          // 4 or fewer PA → summarized in the "Others" line instead of a row
  threatMinPA: 10,
  wildStrikePct: 55,       // pitcher: under 55% strikes, or
  wildFreePct: 14,         // (BB + HBP + WP) per batter faced of 14%+ → WILD
  strikeThrowerPct: 70,
  firstStrikePct: 65,
};

function batterAdvice(s) {
  const parts = [];
  if (s.bip >= RULES.minBipForShade) {
    const side = ["L", "R", "M"].find((k) => pct(s[k], s.bip) >= RULES.shadePct);
    if (side === "M") parts.push(`Stack the middle: ${SIDE_WORDS.M[1]}.`);
    else if (side) parts.push(`Shade ${SIDE_WORDS[side][0]}: ${SIDE_WORDS[side][1]}.`);
    else parts.push("Play straight up.");
  } else parts.push("Play straight up.");
  const ofDeep = s.xbh >= RULES.ofDeepXbh || (s.bip >= RULES.minBipForShade && pct(s.ofAir, s.bip) >= RULES.ofDeepAirPct);
  const ofIn = !ofDeep && s.bip >= RULES.minBipForShade && s.xbh === 0 && pct(s.ofAir, s.bip) <= RULES.ofInMaxAirPct;
  if (ofDeep) parts.push("OF deep.");
  if (ofIn) parts.push("OF in.");
  if (s.bunts >= RULES.buntsForCornersIn) parts.push("Corners in (bunts).");
  if (s.fp >= RULES.minFirstPitches) {
    if (100 - s.takePct >= RULES.dontGrooveSwingPct) parts.push("Don't groove 1st pitch.");
    else if (s.takePct >= RULES.throwStrikeTakePct) parts.push("Throw strike 1.");
  }
  return { text: parts.join(" "), ofDeep, ofIn };
}

function pitcherStats(pas) {
  const s = { bf: 0, pitches: 0, strikes: 0, fp: 0, fpStrike: 0, bb: 0, hbp: 0, k: 0, wp: 0, pb: 0 };
  for (const p of pas) {
    s.wp += p.wp || 0; s.pb += p.pb || 0;
    if (p.np) continue;
    s.bf++;
    const seq = p.p || "";
    s.pitches += seq.length;
    s.strikes += [...seq].filter((c) => c !== "B").length;
    if (seq.length) { s.fp++; if (seq[0] !== "B") s.fpStrike++; }
    else if (p.o === "HBP") s.fp++;
    if (p.o === "BB") s.bb++;
    if (p.o === "HBP") s.hbp++;
    if (p.o === "K") s.k++;
  }
  s.strikePct = pct(s.strikes, s.pitches);
  s.firstStrikePct = pct(s.fpStrike, s.fp);
  s.bbPct = pct(s.bb, s.bf);
  s.freePct = pct(s.bb + s.hbp + s.wp, s.bf);
  s.wild = s.strikePct != null && (s.strikePct < RULES.wildStrikePct || s.freePct >= RULES.wildFreePct);
  return s;
}

function pitcherAdvice(s, unnamed) {
  if (unnamed) return "Name unknown in GC. Judge live.";
  if (s.wild) return s.bb + s.hbp >= s.wp ? "Take until a strike. She walks batters." : "Take until a strike. Run on balls in the dirt.";
  if (s.strikePct >= RULES.strikeThrowerPct) return "Swing early. She throws strikes.";
  if (s.firstStrikePct >= RULES.firstStrikePct) return "Be ready to hit strike 1.";
  return "Work the count.";
}

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00Z`);
  return `${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getUTCMonth()]} ${d.getUTCDate()}`;
};

/**
 * games: game records from buildGameRecord (any order). Returns a JSON-serializable report.
 * opts: { opponentName, numbers: {name: "88"} overrides (e.g. from GC's Team tab), maxGames,
 *         asOf (Warriors game date; default today), lookbackDays (default LOOKBACK_DAYS; 0 = no limit) }
 */
export function buildScoutingReport(games, opts = {}) {
  const lookbackDays = opts.lookbackDays ?? LOOKBACK_DAYS;
  const win = lookbackDays ? gamesInWindow(games, { asOf: opts.asOf || new Date(), lookbackDays }) : { used: games, excluded: [] };
  const sorted = [...win.used].sort((a, b) => `${a.date || ""}${a.key}`.localeCompare(`${b.date || ""}${b.key}`));
  const used = opts.maxGames ? sorted.slice(-opts.maxGames) : sorted;
  if (!used.length) throw new Error(games.length ? `None of the saved games are from the ${lookbackDays} days before this game.` : "No games ingested for this opponent yet.");
  const opponentName = opts.opponentName || used[used.length - 1].scoutedTeam;

  // Uniform numbers: latest box score wins, explicit overrides win over that.
  const numbers = {};
  for (const g of used) Object.assign(numbers, g.numbers || {});
  Object.assign(numbers, opts.numbers || {});

  // ── batters ──
  const byBatter = new Map();
  for (const g of used) for (const p of g.offense) {
    if (p.np) continue;
    const k = p.b || UNNAMED;
    if (!byBatter.has(k)) byBatter.set(k, []);
    byBatter.get(k).push(p);
  }
  const last = used[used.length - 1];
  const lastOrder = [];
  for (const p of last.offense) if (!p.np && p.b && !lastOrder.includes(p.b)) lastOrder.push(p.b);

  const rows = [];
  for (const [name, pas] of byBatter) {
    if (name === UNNAMED) continue;
    const s = batterStats(pas);
    rows.push({ name, num: numbers[name] || null, stats: s, inLast: lastOrder.includes(name), spot: lastOrder.includes(name) ? lastOrder.indexOf(name) + 1 : null });
  }
  const threats = rows.filter((r) => r.stats.pa >= RULES.threatMinPA).sort((a, b) => b.stats.ops - a.stats.ops).slice(0, 3);
  const threatSet = new Set(threats.map((r) => r.name));

  const shown = rows.filter((r) => r.stats.pa > RULES.othersMaxPA || r.inLast);
  const others = rows.filter((r) => !shown.includes(r)).sort((a, b) => b.stats.pa - a.stats.pa);
  shown.sort((a, b) => (a.spot ?? 999) - (b.spot ?? 999) || b.stats.pa - a.stats.pa);

  const toRow = (r) => {
    const adv = batterAdvice(r.stats);
    const tags = [];
    if (threatSet.has(r.name)) tags.push(`TOP 3 THREAT (OPS ~${r.stats.ops.toFixed(2)})`);
    if (r.stats.pa < RULES.fewPA) tags.push("few PA");
    if (!r.inLast) tags.push("not in last game");
    return {
      spot: r.spot, num: r.num, name: r.name, pa: r.stats.pa,
      spray: { L: r.stats.L, M: r.stats.M, R: r.stats.R }, bip: r.stats.bip,
      firstPitchTakePct: r.stats.takePct, ops: +r.stats.ops.toFixed(3),
      avg: r.stats.ab ? +(r.stats.h / r.stats.ab).toFixed(3) : null,
      bunts: r.stats.bunts, xbh: r.stats.xbh, k: r.stats.k, bb: r.stats.bb,
      tags, doThis: adv.text, ofDeep: adv.ofDeep, ofIn: adv.ofIn, threat: threatSet.has(r.name),
    };
  };
  const batters = shown.map(toRow);
  let unnamed = null;
  if (byBatter.has(UNNAMED)) {
    const s = batterStats(byBatter.get(UNNAMED));
    unnamed = { pa: s.pa, spray: { L: s.L, M: s.M, R: s.R }, bip: s.bip, firstPitchTakePct: s.takePct, doThis: batterAdvice(s).text };
  }

  // ── team defense summary ──
  const team = batterStats(used.flatMap((g) => g.offense));
  const defense = [];
  const zp = (k) => pct(team[k], team.bip) ?? 0;
  const heavy = ["L", "R"].find((k) => zp(k) >= 45);
  defense.push(heavy
    ? `Shade OF ${SIDE_WORDS[heavy][0]} (left ${zp("L")}%, mid ${zp("M")}%, right ${zp("R")}%)`
    : `Play OF straight up (left ${zp("L")}%, mid ${zp("M")}%, right ${zp("R")}%)`);
  const label = (b) => `${b.name}${b.num ? ` #${b.num}` : ""}`;
  const deep = batters.filter((b) => b.ofDeep && b.pa >= 5).sort((a, b) => b.ops - a.ops).slice(0, 4);
  if (deep.length) defense.push(`OF deep vs ${deep.map(label).join(", ")}`);
  const bunters = batters.filter((b) => b.bunts >= RULES.buntsForCornersIn);
  if (bunters.length) defense.push(`3B and 1B play in vs ${bunters.map(label).join(", ")} (bunts)`);
  for (const b of batters) {
    if (b.bip < RULES.minBipForShade) continue;
    const side = ["L", "R"].find((k) => pct(b.spray[k], b.bip) >= RULES.hardShadePct);
    if (side) defense.push(`Shade hard vs ${label(b)} (${SIDE_WORDS[side][0]})`);
  }
  const teamSwing = team.fp ? 100 - team.takePct : null;
  if (teamSwing != null) defense.push(teamSwing >= 35
    ? `Don't groove strike 1: they swing at ${teamSwing}% of 1st pitches`
    : `Steal strike 1: they take ${team.takePct}% of 1st pitches`);

  // ── their pitchers ──
  const byPitcher = new Map();
  for (const g of used) for (const p of g.defense) {
    const k = p.pi == null || p.pi === "" ? UNNAMED : p.pi;
    if (!byPitcher.has(k)) byPitcher.set(k, []);
    byPitcher.get(k).push(p);
  }
  const recent = used.slice(-6);
  const starts = {}, lastStart = {};
  recent.forEach((g, idx) => { const k = g.starter || UNNAMED; starts[k] = (starts[k] || 0) + 1; lastStart[k] = idx; });
  // Most starts in the last 6 games; ties go to whoever started most recently.
  const likelyKey = Object.keys(starts).filter((k) => k !== UNNAMED).sort((a, b) => starts[b] - starts[a] || lastStart[b] - lastStart[a])[0] || null;
  const pitchers = [...byPitcher.entries()].map(([k, pas]) => {
    const s = pitcherStats(pas);
    const un = k === UNNAMED;
    return {
      name: un ? "Unnamed" : k, num: un ? null : numbers[k] || null, unnamed: un, likely: k === likelyKey,
      bf: s.bf, strikePct: s.strikePct, firstStrikePct: s.firstStrikePct, bbPct: s.bbPct,
      wp: s.wp, pb: s.pb, k: s.k, bb: s.bb, hbp: s.hbp,
      wild: un ? null : s.wild, doThis: pitcherAdvice(s, un),
    };
  }).filter((p) => p.bf > 0).sort((a, b) => (b.likely - a.likely) || (a.unnamed - b.unnamed) || b.bf - a.bf);

  const dates = used.map((g) => g.date).filter(Boolean).sort();
  return {
    version: 1,
    parserVersion: SCOUTING_PARSER_VERSION,
    opponentName,
    generatedAt: new Date().toISOString(),
    gameCount: used.length,
    lookbackDays: lookbackDays || null,
    excludedGames: win.excluded.length,
    paCount: used.reduce((a, g) => a + g.offense.filter((p) => !p.np).length, 0),
    dateFrom: fmtDate(dates[0]), dateTo: fmtDate(dates[dates.length - 1]),
    games: used.map((g) => ({ key: g.key, date: g.date, dateLabel: g.dateLabel, vs: g.vs })),
    likelyPitcher: likelyKey ? { name: likelyKey, num: numbers[likelyKey] || null, starts: starts[likelyKey], of: recent.length } : null,
    defense,
    batters,
    unnamed,
    others: others.map((r) => ({ name: r.name, num: r.num, pa: r.stats.pa, firstPitchTakePct: r.stats.takePct })),
    pitchers,
    threats: threats.map((r) => label({ name: r.name, num: r.num })),
    warnings: used.flatMap((g) => [...(g.warnings || []), ...(g.checks || []).filter((c) => !c.ok).map((c) => `${g.dateLabel} vs ${g.vs}: ${c.what} parsed ${c.parsed}, GameChanger box score says ${c.box ?? "?"}`)]),
  };
}
